<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { SmartPickerModal, type SmartPickerOption } from '@yuksalish/ui'
import { formatApiError } from '@yuksalish/api-client'
import { useFinanceApi } from '../composables/useFinanceApi'
import type { ProductOption } from '../types/finance'

/**
 * Product picker for the costing page. The products endpoint is searchable and
 * paginated, so search and paging are served by the backend.
 */
const props = withDefaults(
  defineProps<{
    open: boolean
    title?: string
    searchPlaceholder?: string
    emptyMessage?: string
    loadingMessage?: string
    selectedId?: string | number | null
  }>(),
  {
    title: '',
    searchPlaceholder: '',
    emptyMessage: '',
    loadingMessage: '',
    selectedId: null,
  },
)

const emit = defineEmits<{
  close: []
  select: [product: ProductOption]
}>()

const { t } = useI18n()
const { searchProducts } = useFinanceApi()

const PAGE_SIZE = 20

const searchValue = ref('')
const products = ref<ProductOption[]>([])
const loading = ref(false)
const error = ref('')
const page = ref(1)
const total = ref(0)
const totalPages = ref(1)

let searchTimer: ReturnType<typeof setTimeout> | undefined

const items = computed<SmartPickerOption[]>(() =>
  products.value.map((product) => ({
    id: product.id,
    title: product.name,
    subtitle: product.sku || product.internal_sku_code || undefined,
    meta: product.type || undefined,
  })),
)

const rangeLabel = computed(() => {
  if (!total.value) return ''
  const from = (page.value - 1) * PAGE_SIZE + 1
  const to = Math.min(page.value * PAGE_SIZE, total.value)
  return t('finance.picker.pageRange', { from, to, total: total.value })
})

async function load() {
  loading.value = true
  error.value = ''
  try {
    const result = await searchProducts({
      search: searchValue.value.trim() || undefined,
      is_active: 1,
      page: page.value,
      per_page: PAGE_SIZE,
    })
    products.value = result.items
    total.value = result.meta.total
    totalPages.value = Math.max(1, result.meta.total_pages)
  } catch (cause) {
    error.value = formatApiError(cause)
    products.value = []
    total.value = 0
    totalPages.value = 1
  } finally {
    loading.value = false
  }
}

function goToPage(next: number) {
  page.value = Math.min(Math.max(1, next), totalPages.value)
  void load()
}

watch(searchValue, () => {
  if (searchTimer) clearTimeout(searchTimer)
  searchTimer = setTimeout(() => {
    page.value = 1
    void load()
  }, 300)
})

watch(
  () => props.open,
  (isOpen) => {
    if (!isOpen) return
    searchValue.value = ''
    page.value = 1
    void load()
  },
)

onMounted(() => {
  if (props.open) void load()
})
</script>

<template>
  <SmartPickerModal
    :open="open"
    :title="title || t('finance.picker.productTitle')"
    :eyebrow="t('finance.picker.productEyebrow')"
    :search-label="t('finance.picker.productSearch')"
    :search-placeholder="searchPlaceholder || t('finance.picker.productPlaceholder')"
    :search-value="searchValue"
    :loading="loading"
    :error-message="error"
    :empty-message="emptyMessage || t('finance.picker.productEmpty')"
    :items="items"
    :page="page"
    :total-pages="totalPages"
    :selected-id="selectedId"
    :page-label="rangeLabel"
    :close-aria-label="t('finance.picker.close')"
    :retry-label="t('finance.picker.retry')"
    :previous-label="t('finance.picker.previous')"
    :next-label="t('finance.picker.next')"
    :cancel-label="t('finance.picker.cancel')"
    @close="emit('close')"
    @retry="load"
    @previous="goToPage(page - 1)"
    @next="goToPage(page + 1)"
    @select="(option: SmartPickerOption) => {
      const match = products.find((product) => String(product.id) === String(option.id))
      if (match) emit('select', match)
    }"
    @update:search-value="(value: string) => (searchValue = value)"
  >
    <template #loading>{{ loadingMessage || t('finance.picker.productLoading') }}</template>
  </SmartPickerModal>
</template>
